import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  Alert,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { getUserInvites } from '../../store/slices/usersSlice';
import { Invite } from '../../types';
import dayjs from 'dayjs';

interface UserInvitesModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  username: string;
}

const UserInvitesModal: React.FC<UserInvitesModalProps> = ({
  open,
  onClose,
  userId,
  username,
}) => {
  const dispatch = useDispatch();
  const { userInvites, loading } = useSelector((state: RootState) => state.users);

  useEffect(() => {
    if (open && userId) {
      dispatch(getUserInvites(userId) as any);
    }
  }, [open, userId, dispatch]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
    }).format(amount);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle>
        <Typography variant="h6">Invites for {username}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : userInvites.length === 0 ? (
          <Alert severity="info">No invites found for this user.</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Inviter</TableCell>
                  <TableCell>Invitee Phone</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reward Amount</TableCell>
                  <TableCell>Created Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userInvites.map((invite: Invite) => (
                  <TableRow key={invite.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {invite.inviter?.username || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {invite.inviter?.phoneNumber || 'No Phone'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {invite.inviteePhoneNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={invite.status}
                        color={getStatusColor(invite.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold" color="success.main">
                        {formatCurrency(invite.rewardAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {dayjs(invite.createdAt).format('MMM DD, YYYY HH:mm')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserInvitesModal; 